export interface InvoiceData {
    id: number;
    status: InvoiceStatus;
    source_file_name: string;
    file_url: string;
    custom_file_url: string;
    supplier: string;
    buyer: string;
    number: string;
    date_of_sale: string;
    due_date: string | null;
    paid_on: string | null;
    total_amount: number | string;
    subtotal_amount: number | string;
    vat: number | string;
    currency: string;
    iban: string | null;
    parsed_text: string;
    parsed_json: ParsedInvoiceJson;
    user_ip: string;
    error_message: string | null;
    reviewed_at: string | null;
    spreadsheet_row_number: number | null;
    accountants_notes: string | null;
    sender_email: string | null;
    manager_agreed_to_pay: boolean | null;
    cash_register: string | null;
    expense_item: string | null;
    activity_area: string | null;
    division: string | null;
    project: string | null;
    notes: string | null;
    saved_letter_body: string | null;
    created_at: string;
    updated_at: string;
}

export interface ParsedInvoiceJson {
    supplier: string;
    buyer: string;
    number: string;
    date_of_sale: string;
    due_date: string | null;
    paid_on: string | null;
    total_amount: string;
    subtotal_amount: string;
    vat: string;
    currency: string;
    iban: string | null;
    seller_email: string | null;
    cash_register: string | null;
    expense_item: string | null;
    activity_area: string | null;
    division: string | null;
    project: string | null;
}

interface Link {
	url: string | null;
	label: string;
	active: boolean;
}

interface Links {
	first: string;
	last: string;
	prev: string | null;
	next: string | null;
}

interface Meta {
	current_page: number;
	from: number;
	last_page: number;
	links: Link[];
	path: string;
	per_page: number;
	to: number;
	total: number;
}

export interface List<T> {
	data: T[];
	links: Links;
	meta: Meta;
}

export interface Single<T> {
	data: T
}

export enum InvoiceStatus {
	failed='failed',
	ready_to_review='ready_to_review',
	file_parsed_by_ocr='file_parsed_by_ocr',
	reviewed='reviewed',
	file_saved='file_saved'
}

export interface InvoiceFormValues {
	supplier: string;
	buyer: string;
	number: string;
	date_of_sale: string;
	due_date: string;
	paid_on: string;
	total_amount: string | number;
	subtotal_amount: string | number;
	vat: string | number;
	currency: string;
	iban: string;
	cash_register: string;
	expense_item: string;
	activity_area: string;
	division: string;
	project: string;
	notes: string;
}

export interface Variant {
	lang: string;
	full_name: string;
	part_name: string;
}

export interface ListItem {
	[code: string]: OptionItem;
}

export interface OptionsRes {
	cash_registers: ListItem;
	expense_items: ListItem;
	activity_areas: ListItem;
	divisions: ListItem;
	projects: ListItem;
}

export interface OptionList {
	[key: string]: OptionItem;
}

export interface OptionItem {
	code: string;
	full_name: string;
	other_variants: Variant[];
	part_name: string;
}

export enum TabKeys {
	incoming = 'incoming',
	pending = 'pending',
	processed = 'processed'
}

export interface RulesRes {
	[key: string]: {
		[code: string]: {[key: string]: string[]}
	}
}

export interface RuleReq {
	selected_field: string;
	field_field_value: string;
	related_field: string;
	related_field_value: string;
}

export type DeleteOptionReq = PostOptionReq['params'];

export interface PostOptionReq {
	params: {
		option: string;
		option_code: string;
	},
	data: {
		code: string;
		name: string;
		otherVariants: {
			lang: string;
			name: string;
		}[];
	}
}

export type PostNewOptionReq = Omit<PostOptionReq, 'params'> & {
	params: Omit<PostOptionReq['params'], 'option_code'>;
};

export const expectedKeys = {
	cash_registers: 'cash_register',
	activity_areas: 'activity_area',
	divisions: 'division',
	expense_items: 'expense_item',
	projects: 'project'
} as const

export interface SelectOption {
	value: string;
	label: string;
}

export const translatedKeys = {
	cash_registers: 'Каси',
	expense_items: 'Статті витрат',
	activity_areas: 'Області діяльності',
	divisions: 'Підрозділи',
	projects: 'Проекти'
} as const

export interface User {
	id: number;
	name: string;
	email: string;
	email_verified_at: string | null;
	created_at: string;
	updated_at: string;
}

export interface LoginRequest {
	email: string;
	password: string;
}

export interface LoginResponse {
	access_token: string;
	token_type: string;
	user: User;
}

export interface Role {
	id: number;
	name: string;
	description: string;
}

export interface Permission {
	id: number;
	name: string;
	description: string;
}

export interface GetMeResponse {
	user: User;
	roles: Role[];
	permissions: Permission[];
}

export interface AuthContextType {
	user: User | null;
	token: string | null;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	logoutAll: () => Promise<void>;
	isAuthenticated: boolean;
	loading: boolean;
}
